import { Button, Container, Label, TextAreaInput } from '@playcanvas/pcui';
import { Events } from '../events';
import { Tooltips } from './tooltips';

class AIEditPanel extends Container {
    private uploadedImages: File[] = [];
    private previewContainer: Container;
    private generateButton: Button;
    private promptInput: TextAreaInput;
    private resultContainer: Container;
    private screenshotPreviewContainer: Container;
    private resultImage: HTMLImageElement | null = null;
    private screenshotImage: HTMLImageElement | null = null;
    private isCollapsed: boolean = false;
    private collapseButton: Button;
    private contentContainer: Container;

    constructor(events: Events, tooltips: Tooltips, args = {}) {
        args = {
            ...args,
            id: 'ai-edit-panel',
            class: 'ai-edit-panel'
        };

        super(args);

        // Prevent event propagation
        this.dom.addEventListener('pointerdown', (event) => {
            event.stopPropagation();
        });

        // Header with collapse button
        const header = new Container({
            class: 'ai-panel-header'
        });

        this.collapseButton = new Button({
            class: 'ai-panel-collapse-btn',
            text: '◀'
        });

        const headerLabel = new Label({
            text: 'AI Edit',
            class: 'ai-panel-header-label'
        });

        header.append(headerLabel);
        header.append(this.collapseButton);

        // Content container (collapsible)
        this.contentContainer = new Container({
            class: 'ai-panel-content'
        });

        // Upload section
        const uploadSection = new Container({
            class: 'ai-panel-section'
        });

        const uploadSectionLabel = new Label({
            text: 'Reference Images (up to 4)',
            class: 'ai-section-label'
        });

        const uploadButton = new Button({
            text: '+ Upload Images',
            class: 'ai-upload-btn'
        });

        // Hidden file input
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.multiple = true;
        fileInput.style.display = 'none';

        // Preview container for uploaded images
        this.previewContainer = new Container({
            class: 'ai-preview-container'
        });

        uploadSection.append(uploadSectionLabel);
        uploadSection.append(uploadButton);
        uploadSection.append(this.previewContainer);

        // Prompt section
        const promptSection = new Container({
            class: 'ai-panel-section'
        });

        const promptSectionLabel = new Label({
            text: 'Edit Instruction',
            class: 'ai-section-label'
        });

        this.promptInput = new TextAreaInput({
    // Make the placeholder teach the user the "First/Second" logic
    placeholder: 'e',
    class: 'ai-prompt-input',
    resizable: 'vertical'
});
        promptSection.append(promptSectionLabel);
        promptSection.append(this.promptInput);

        // Generate button
        this.generateButton = new Button({
            text: 'Capture & Generate',
            class: 'ai-generate-btn'
        });
        this.generateButton.enabled = false;

        // Screenshot Preview section
        const screenshotPreviewSection = new Container({
            class: 'ai-panel-section'
        });

        const screenshotPreviewLabel = new Label({
            text: 'Current Render',
            class: 'ai-section-label'
        });

        this.screenshotPreviewContainer = new Container({
            class: 'ai-screenshot-preview-container'
        });

        const screenshotPlaceholder = new Label({
            text: 'Screenshot will appear after Generate',
            class: 'ai-preview-placeholder'
        });

        this.screenshotPreviewContainer.append(screenshotPlaceholder);
        screenshotPreviewSection.append(screenshotPreviewLabel);
        screenshotPreviewSection.append(this.screenshotPreviewContainer);

        // Result section
        const resultSection = new Container({
            class: ['ai-panel-section', 'ai-result-section']
        });

        const resultSectionLabel = new Label({
            text: 'Generated Result',
            class: 'ai-section-label'
        });

        this.resultContainer = new Container({
            class: 'ai-result-container'
        });

        const resultPlaceholder = new Label({
            text: 'Generated image will appear here',
            class: 'ai-result-placeholder'
        });

        this.resultContainer.append(resultPlaceholder);
        resultSection.append(resultSectionLabel);
        resultSection.append(this.resultContainer);

        // Append all sections
        this.contentContainer.append(uploadSection);
        this.contentContainer.append(promptSection);
        this.contentContainer.append(this.generateButton);
        this.contentContainer.append(screenshotPreviewSection);
        this.contentContainer.append(resultSection);

        // Append to main panel
        this.append(header);
        this.append(this.contentContainer);

        // Event handlers
        this.collapseButton.on('click', () => {
            this.isCollapsed = !this.isCollapsed;
            this.contentContainer.class[this.isCollapsed ? 'add' : 'remove']('collapsed');
            this.collapseButton.text = this.isCollapsed ? '▶' : '◀';
            this.dom.classList[this.isCollapsed ? 'add' : 'remove']('collapsed');
        });

        uploadButton.on('click', () => {
            fileInput.click();
        });

        fileInput.addEventListener('change', (e) => {
            const files = Array.from((e.target as HTMLInputElement).files || []);
            const availableSlots = 4 - this.uploadedImages.length;
            const filesToAdd = files.slice(0, availableSlots);
            this.uploadedImages.push(...filesToAdd);
            this.updateImagePreviews();
            fileInput.value = '';
        });

        this.promptInput.on('change', (value: string) => {
            this.generateButton.enabled = value.trim().length > 0;
        });

        this.generateButton.on('click', async () => {
            await this.handleGenerate(events);
        });

        tooltips.register(uploadButton, 'Upload reference images (max 4)', 'left');
        tooltips.register(this.generateButton, 'Capture scene and generate AI-edited image', 'left');
    }

    private updateImagePreviews() {
        this.previewContainer.clear();
        this.uploadedImages.forEach((file, index) => {
            const previewItem = new Container({
                class: 'ai-preview-item'
            });
            const img = document.createElement('img');
            img.className = 'ai-preview-image';
            const reader = new FileReader();
            reader.onload = (e) => {
                img.src = e.target?.result as string;
            };
            reader.readAsDataURL(file);
            const removeBtn = document.createElement('button');
            removeBtn.className = 'ai-preview-remove';
            removeBtn.innerHTML = '×';
            removeBtn.onclick = () => {
                this.uploadedImages.splice(index, 1);
                this.updateImagePreviews();
            };
            const previewWrapper = document.createElement('div');
            previewWrapper.className = 'ai-preview-wrapper';
            previewWrapper.appendChild(img);
            previewWrapper.appendChild(removeBtn);
            previewItem.dom.appendChild(previewWrapper);
            this.previewContainer.append(previewItem);
        });
    }

    private async handleGenerate(events: Events) {
        try {
            this.generateButton.enabled = false;
            this.generateButton.text = 'Capturing...';

            const prompt = this.promptInput.value;
            const width = 1920;
            const height = 1080;
            
            console.log('📸 Capturing screenshot...');
            const screenshotData = await events.invoke('render.offscreen', width, height);

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            const imgData = new ImageData(new Uint8ClampedArray(screenshotData), width, height);
            ctx.putImageData(imgData, 0, 0);
            const screenshotBase64 = canvas.toDataURL('image/png');

            // Show screenshot preview
            this.displayScreenshotPreview(screenshotBase64);

            this.generateButton.text = 'Generating...';
            console.log('🤖 Sending to AI...');

            const referenceImages = await Promise.all(
                this.uploadedImages.map(file => this.fileToBase64(file))
            );

            const result = await this.callAIAPI(screenshotBase64, referenceImages, prompt);
            this.displayResult(result);

            this.generateButton.text = 'Capture & Generate';
            this.generateButton.enabled = true;

        } catch (error) {
            console.error('Generation failed:', error);
            this.generateButton.text = 'Failed - Try Again';
            this.generateButton.enabled = true;
            
            alert(`Generation failed: ${error.message}`);
        }
    }

    private displayScreenshotPreview(imageBase64: string) {
        this.screenshotPreviewContainer.clear();
        this.screenshotImage = document.createElement('img');
        this.screenshotImage.className = 'ai-screenshot-image';
        this.screenshotImage.src = imageBase64;
        this.screenshotPreviewContainer.dom.appendChild(this.screenshotImage);
    }

    private async fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    private async callAIAPI(screenshot: string, referenceImages: string[], prompt: string): Promise<string> {
       const BACKEND_URL = ' https://gemini-image-server-worker.xor3dai.workers.dev';  // For local Worker testing
        
        try {
            console.log('🍌 Sending request to backend server...');
            console.log('Prompt:', prompt);
            console.log('Reference images:', referenceImages.length);
            
            const response = await fetch(`${BACKEND_URL}/api/generate-image`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    screenshot: screenshot,
                    referenceImages: referenceImages,
                    prompt: prompt
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Backend Error:', errorData);
                
                if (response.status === 429) {
                    const waitTime = errorData.retryAfter || 60;
                    throw new Error(`Rate limit exceeded. Please wait ${waitTime} seconds and try again.`);
                }
                
                throw new Error(errorData.message || `Request failed: ${response.statusText}`);
            }

            const data = await response.json();
            
            if (!data.success || !data.image) {
                throw new Error('No image received from server');
            }
            
            console.log('✅ Image generated successfully via backend!');
            console.log('Model used:', data.model);
            
            return data.image;

        } catch (error) {
            console.error('❌ AI API Error:', error);
            
            if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
                throw new Error('Cannot connect to backend server. Make sure the server is running on http://localhost:3001');
            }
            
            throw error;
        }
    }

    private displayResult(imageBase64: string) {
        this.resultContainer.clear();
        this.resultImage = document.createElement('img');
        this.resultImage.className = 'ai-result-image';
        this.resultImage.src = imageBase64;

        const downloadBtn = new Button({
            text: 'Download',
            class: 'ai-download-btn'
        });

        downloadBtn.on('click', () => {
            const link = document.createElement('a');
            link.download = `ai-edited-${Date.now()}.png`;
            link.href = imageBase64;
            link.click();
        });

        const applyBtn = new Button({
            text: 'Apply to Scene',
            class: 'ai-apply-btn'
        });

        applyBtn.on('click', () => {
            console.log('Apply to scene - to be implemented');
        });

        const buttonContainer = new Container({
            class: 'ai-result-buttons'
        });

        buttonContainer.append(downloadBtn);
        buttonContainer.append(applyBtn);

        this.resultContainer.dom.appendChild(this.resultImage);
        this.resultContainer.append(buttonContainer);
    }
}

export { AIEditPanel };